import { createContext, ReactNode, useContext, useEffect, useReducer, useRef } from 'react'
import { useRouter } from 'next/router'
import toast from 'react-hot-toast'
import { useMutation, useQueryClient } from 'react-query'
import { getCookie, setCookie, deleteCookie } from 'cookies-next'
import { AxiosError, HttpStatusCode } from 'axios'
import {
  useParentGameStore,
  useSelectionsStore,
  useUnsavedChangesGuardStore,
  useUserStore,
} from '@/store/store'
import { AuthUser, ActionMap, AuthState, JWTContextType } from 'types/auth'
import { AuthBEAPIClient, setAccessToken } from 'api/BEClient'
import { STATUS_CODES_TO_ERRORS } from 'api/constants'
import { profileService } from 'api/api'
import { RegisterDTO, UserInfo } from 'types/types'
import { isGameStarted } from 'types/utils'
import { useLocalSelectionsState } from 'hooks/useLocalSelectionsState'

enum Types {
  Initial = 'INITIALIZE',
  Login = 'LOGIN',
  Logout = 'LOGOUT',
  Register = 'REGISTER',
  Reload = 'RELOAD',
}

type JWTAuthPayload = {
  [Types.Initial]: {
    isAuthenticated: boolean
    user: AuthUser
  }
  [Types.Login]: {
    user: AuthUser
  }
  [Types.Reload]: {
    user: AuthUser
  }
  [Types.Logout]: undefined
  [Types.Register]: {
    user: AuthUser
  }
}

// temp, wil be changed to dynamic
const COOKIE_TTL = 60 * 60 * 24 * 30 // 30 days

type JWTActions = ActionMap<JWTAuthPayload>[keyof ActionMap<JWTAuthPayload>]

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
}

const setSession = (accessToken: string | null) => {
  if (accessToken) {
    setCookie('accessToken', accessToken, {
      maxAge: COOKIE_TTL,
      sameSite: 'none',
      secure: process.env.NODE_ENV === 'production',
    })
  } else {
    deleteCookie('accessToken', {
      sameSite: process.env.NODE_ENV === 'production' ? 'none' : 'lax',
      secure: process.env.NODE_ENV === 'production',
    })
    delete AuthBEAPIClient.defaults.headers.common.Authorization
  }
}

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      }
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      }
    case 'RELOAD':
      return {
        ...state,
        user: action.payload.user,
      }
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      }

    case 'REGISTER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      }

    default:
      return state
  }
}

const AuthContext = createContext<JWTContextType>({
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  isLoginLoading: false,
  mutateLogin: () => {},
  mutateRegister: () => {},
  isRegisterLoading: false,
  logout: (redirectUrl: string): Promise<void> => {
    throw new Error('Function not implemented.')
  },
  resetPassword: (email: string): Promise<void> => {
    throw new Error('Function not implemented.')
  },
  reloadUser: (): Promise<void> => {
    throw new Error('Function not implemented.')
  },
  handleSetToken: ({ access_token }: { access_token: string }) => {
    throw new Error('Function not implemented.')
  },
})

// Logs the error's own message/stack as the primary value (present for both
// network errors and non-Axios throws, unlike `e.response.data`), and only
// appends response data when one actually exists. Never logs the raw Axios
// error itself — its `config`/`request` carry the auth header.
function logApiError(label: string, e: unknown) {
  const err = e as AxiosError<any>
  const primary = err?.stack ?? err?.message ?? String(err)
  const responseData = err?.response?.data
  // eslint-disable-next-line no-console
  console.error(`${label}:`, primary, ...(responseData !== undefined ? [{ responseData }] : []))
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const [state, dispatch] = useReducer(JWTReducer, initialState)
  const setSelections = useSelectionsStore((state) => state.setSelections)
  const setUser = useUserStore((state) => state.setUser)
  const parentGame = useParentGameStore((state) => state.parentGame)
  const setLogoutInProgress = useUnsavedChangesGuardStore((state) => state.setLogoutInProgress)
  const { clearLocalSelections, clearTiebreakers } = useLocalSelectionsState()

  const isInitializing = useRef(false)

  useEffect(() => {
    if (isInitializing.current) return
    isInitializing.current = true

    const initialize = async () => {
      try {
        const accessToken = getCookie('accessToken', {
          sameSite: process.env.NODE_ENV === 'production' ? 'none' : 'lax',
          secure: process.env.NODE_ENV === 'production',
        })

        if (accessToken) {
          setSession(accessToken)
          AuthBEAPIClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`
          const response = await AuthBEAPIClient.get<UserInfo>('/users/me')
          if (response.data.id) {
            setUser(response.data)
          }
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: true,
              user: response.data,
            },
          })
        } else {
          dispatch({
            type: Types.Initial,
            payload: {
              isAuthenticated: false,
              user: null,
            },
          })
        }
      } catch (err) {
        dispatch({
          type: Types.Initial,
          payload: {
            isAuthenticated: false,
            user: null,
          },
        })
      }
    }

    initialize()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getMe = async () => {
    try {
      const me_response = await AuthBEAPIClient.get<UserInfo>('/users/me')
      if (me_response.data.id) {
        setUser(me_response.data)
      }
      dispatch({
        type: Types.Login,
        payload: {
          user: me_response.data,
        },
      })
    } catch (e) {
      logApiError('Failed to get users/me data', e)
    }
  }

  const { mutate: mutateRegister, isLoading: isRegisterLoading } = useMutation({
    mutationFn: (data: RegisterDTO) => profileService.register(data),
    onError: (e: AxiosError<any>, vars: any) => {
      return e.response?.status && e.response?.status > 500
        ? (toast.error('Failed to register, profile service error') as unknown as void)
        : (toast.error(
            STATUS_CODES_TO_ERRORS[e.response?.status as keyof typeof STATUS_CODES_TO_ERRORS],
          ) as unknown as void)
    },
    onSuccess: (data: { access_token: string; refresh_token: string }, vars: any) => {
      setCookie('accessToken', data.access_token, {
        maxAge: COOKIE_TTL,
        sameSite: process.env.NODE_ENV === 'production' ? 'none' : 'lax',
        secure: process.env.NODE_ENV === 'production',
      })
      setAccessToken(data.access_token)
      setSession(data.access_token)
      getMe()
    },
  })

  const handleSetToken = ({ access_token }: { access_token: string }) => {
    setCookie('accessToken', access_token, {
      maxAge: COOKIE_TTL,
      sameSite: process.env.NODE_ENV === 'production' ? 'none' : 'lax',
      secure: process.env.NODE_ENV === 'production',
    })
    setAccessToken(access_token)
    setSession(access_token)
    getMe()
  }

  const logoutInProgressRef = useRef<string | null>(null)

  const { mutate: mutateLogin, isLoading: isLoginLoading } = useMutation({
    mutationFn: (data: { email: string; password: string; organization_id: string }) =>
      profileService.login(data.email, data.password, data.organization_id),
    onError: (e: AxiosError<any>) => {
      logApiError('Login failed', e)
    },
    onSuccess: (data: { access_token: string; refresh_token: string }, vars: any) => {
      handleSetToken({ access_token: data.access_token })
    },
  })

  const logout = async (redirectUrl: string) => {
    logoutInProgressRef.current = redirectUrl
    // Logout intentionally bypasses the unsaved-changes route guard — its own
    // router.push would otherwise get cancelled behind a confirm modal while
    // this function's cleanup below runs anyway, leaving a broken
    // half-logged-out state (see useUnsavedChangesGuardStore).
    setLogoutInProgress(true)
    router.push(redirectUrl)
    setSession(null)
    setSelections(null)
    dispatch({ type: Types.Logout })
    deleteCookie('accessToken', {
      sameSite: process.env.NODE_ENV === 'production' ? 'none' : 'lax',
      secure: process.env.NODE_ENV === 'production',
    })
    clearLocalSelections()
    clearTiebreakers()
    setUser(null)
    sessionStorage.removeItem('mini-league-id')
    sessionStorage.removeItem('predictions')
    sessionStorage.removeItem('tiebreaker')

    const gameStarted = isGameStarted(parentGame)

    if (gameStarted) {
      router.reload()
    }

    window.setTimeout(() => {
      logoutInProgressRef.current = null
      // Deferred until the redirect has had time to land — clearing
      // immediately, while the outgoing page's still-mounted queries are
      // observed, can trigger refetches with no auth header (setSession(null)
      // above already stripped it), surfacing as 401 noise mid-navigation.
      // Disabled queries (gated on isAuthenticated) still serve their last
      // cached result instead of clearing it on their own — without this, a
      // subsequent session in the same tab can get the previous user's
      // cached game/selections data handed straight back to it.
      queryClient.clear()
      setLogoutInProgress(false)
    }, 1000)
  }

  useEffect(() => {
    if (typeof window !== 'undefined' && 'cookieStore' in window) {
      const handleCookieChange = async (event: any) => {
        const deletedCookie = event.deleted?.find((cookie: any) => cookie.name === 'accessToken')

        if (deletedCookie) {
          if (logoutInProgressRef.current) {
            return
          }

          const isPrizeDrawView = router.pathname.includes('/prize-draw')
          const redirectUrl = isPrizeDrawView ? router.asPath : `/${router.query.slug}/landing`
          await logout(redirectUrl)
        }
      }

      ;(window as any).cookieStore.addEventListener('change', handleCookieChange)

      return () => {
        ;(window as any).cookieStore?.removeEventListener('change', handleCookieChange)
      }
    }
  }, [router])

  const reloadUser = async () => {
    const me_response = await AuthBEAPIClient.get('/users/me')
    dispatch({
      type: Types.Reload,
      payload: {
        user: me_response.data,
      },
    })
    if (me_response.status === HttpStatusCode.Ok) {
      setUser(me_response.data)
    }
  }

  const resetPassword = async (email: string) => {
    await AuthBEAPIClient.post('/users/password/reset', { email })
  }

  return (
    <AuthContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        ...state,
        mutateLogin,
        isLoginLoading,
        logout,
        mutateRegister,
        isRegisterLoading,
        resetPassword,
        reloadUser,
        handleSetToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
